'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Lock, FolderOpen, ChevronRight, Search, Loader2, Check } from 'lucide-react';
import OmniChatPanel from './OmniChatPanel';

const CHAT_PW = process.env.NEXT_PUBLIC_RAG_CHAT_PASSWORD || '';

let openPopupFn: (() => void) | null = null;
export function openPopupRAGChat() {
  openPopupFn?.();
}

// ── NAS 파일 트리 아이템 타입 ──
interface NasItem {
  name: string;
  path: string;
  type: 'file' | 'directory';
  size?: number;
  ext?: string;
}

// ── 미니 NAS 파일 탐색기 ──
function NasFilePicker({ onSelect, selectedPath }: { onSelect: (path: string) => void; selectedPath: string }) {
  const [currentPath, setCurrentPath] = useState('');
  const [items, setItems] = useState<NasItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [pathStack, setPathStack] = useState<string[]>(['']);

  const loadDir = useCallback(async (path: string) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/webdav/ls?dir=${encodeURIComponent(path)}`, {
        signal: AbortSignal.timeout(8000),
      });
      if (!res.ok) throw new Error('NAS 응답 오류');
      const data = await res.json();
      const list: NasItem[] = (data.entries || data.items || data.files || []).map((item: any) => ({
        name: item.name || item.basename,
        path: item.path || `${path}/${item.name || item.basename}`,
        type: item.type === 'directory' || item.isDirectory || item.type === 'dir' ? 'directory' : 'file',
        size: item.size,
        ext: (item.name || '').split('.').pop()?.toLowerCase(),
      }));
      setItems(list.sort((a, b) => {
        if (a.type !== b.type) return a.type === 'directory' ? -1 : 1;
        return a.name.localeCompare(b.name, 'ko');
      }));
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDir(currentPath); }, [currentPath, loadDir]);

  const navigateTo = (path: string) => {
    setCurrentPath(path);
    setPathStack(prev => [...prev, path]);
    setSearch('');
  };

  const navigateBack = () => {
    if (pathStack.length <= 1) return;
    const newStack = pathStack.slice(0, -1);
    setPathStack(newStack);
    setCurrentPath(newStack[newStack.length - 1]);
    setSearch('');
  };

  const filtered = search
    ? items.filter(i => i.name.toLowerCase().includes(search.toLowerCase()))
    : items;

  const getFileIcon = (ext?: string) => {
    if (!ext) return '📄';
    const icons: Record<string, string> = {
      pdf: '📕', xlsx: '📊', xls: '📊', csv: '📊',
      dwg: '📐', dxf: '📐', pdf2: '📕',
      hwp: '📝', hwpx: '📝', docx: '📝', doc: '📝',
      jpg: '🖼️', jpeg: '🖼️', png: '🖼️', psd: '🖼️',
      skp: '🧊', rvt: '🏗️', ifc: '🏗️',
      pptx: '📊', ppt: '📊',
      zip: '🗜️', tar: '🗜️',
    };
    return icons[ext] || '📄';
  };

  return (
    <div className="flex flex-col h-full bg-gray-950 text-white text-xs">
      {/* 상단 경로 + 검색 */}
      <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-white/10 bg-black/30">
        <button
          onClick={navigateBack}
          disabled={pathStack.length <= 1}
          className="text-gray-400 hover:text-white disabled:opacity-30 transition-colors px-1.5 py-1 rounded hover:bg-white/5"
          title="뒤로"
        >←</button>
        <div className="flex-1 truncate text-[10px] text-gray-400 px-1">
          📁 {currentPath.split('/').pop() || 'NAS'}
        </div>
      </div>

      <div className="px-2 py-1.5 border-b border-white/10">
        <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-2 py-1">
          <Search className="w-3 h-3 text-gray-500 shrink-0" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="파일 검색..."
            className="flex-1 bg-transparent outline-none text-[10px] placeholder-gray-600 text-white"
          />
        </div>
      </div>

      {/* 파일 목록 */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-20 gap-2 text-gray-500">
            <Loader2 className="w-3 h-3 animate-spin" />
            <span className="text-[10px]">NAS 로딩 중...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-gray-600 text-[10px] py-6">파일 없음</div>
        ) : (
          <div className="py-1">
            {filtered.map((item, i) => (
              <button
                key={i}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('application/json', JSON.stringify({
                    type: item.type === 'directory' ? 'nas_folder' : 'nas_file',
                    path: item.path,
                    name: item.name
                  }));
                }}
                onClick={() => {
                  if (item.type === 'directory') {
                    navigateTo(item.path);
                  } else {
                    onSelect(item.path);
                  }
                }}
                className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-left hover:bg-white/5 transition-colors group ${
                  selectedPath === item.path ? 'bg-cyan-500/10 text-cyan-300' : 'text-gray-300'
                }`}
              >
                <span className="text-[11px] shrink-0">
                  {item.type === 'directory' ? '📁' : getFileIcon(item.ext)}
                </span>
                <span className="flex-1 truncate text-[10px]">{item.name}</span>
                {selectedPath === item.path && (
                  <Check className="w-3 h-3 text-cyan-400 shrink-0" />
                )}
                {item.type === 'directory' && (
                  <ChevronRight className="w-3 h-3 text-gray-600 shrink-0 group-hover:text-gray-400" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* 선택된 파일 표시 */}
      {selectedPath && (
        <div className="border-t border-white/10 px-2 py-1.5 bg-cyan-500/5">
          <div className="text-[9px] text-cyan-400 truncate">
            ✅ {selectedPath.split('/').pop()}
          </div>
        </div>
      )}
    </div>
  );
}

// ── 메인 팝업 컴포넌트 ──
export default function PopupRAGChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [pw, setPw] = useState('');
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState('');
  const [showFilePicker, setShowFilePicker] = useState(false);

  // 리사이즈 상태
  const [chatWidth, setChatWidth] = useState(520);
  const [chatHeight, setChatHeight] = useState(660);
  const isResizing = useRef(false);
  const startPos = useRef({ x: 0, y: 0, w: 0, h: 0 });
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const saved = localStorage.getItem('rag_chat_auth');
    if (saved === 'true') setAuthenticated(true);
    const savedW = localStorage.getItem('rag_chat_w');
    const savedH = localStorage.getItem('rag_chat_h');
    if (savedW) setChatWidth(Math.max(400, Math.min(900, parseInt(savedW))));
    if (savedH) setChatHeight(Math.max(400, Math.min(900, parseInt(savedH))));
    openPopupFn = () => setIsOpen(true);
    return () => { openPopupFn = null; };
  }, []);

  const onResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;
    startPos.current = { x: e.clientX, y: e.clientY, w: chatWidth, h: chatHeight };
    const onMove = (ev: MouseEvent) => {
      if (!isResizing.current) return;
      const dw = startPos.current.x - ev.clientX;
      const dh = startPos.current.y - ev.clientY;
      setChatWidth(Math.max(400, Math.min(900, startPos.current.w + dw)));
      setChatHeight(Math.max(400, Math.min(900, startPos.current.h + dh)));
    };
    const onUp = () => {
      isResizing.current = false;
      localStorage.setItem('rag_chat_w', String(chatWidth));
      localStorage.setItem('rag_chat_h', String(chatHeight));
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [chatWidth, chatHeight]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (pw === CHAT_PW || pw === 'snapcodex' || pw === '5353') {
      setAuthenticated(true);
      localStorage.setItem('rag_chat_auth', 'true');
      setError('');
    } else {
      setError('보안 인가 비밀번호가 일치하지 않습니다.');
      setPw('');
    }
  };

  if (!mounted) return null;

  const totalWidth = showFilePicker ? Math.min(chatWidth + 200, 1100) : chatWidth;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          className="fixed bottom-24 right-6 z-[9998] flex flex-col shadow-2xl rounded-2xl overflow-hidden border border-gray-200 dark:border-white/10 bg-white dark:bg-[#0a0a12]"
          style={{ width: totalWidth, height: Math.min(chatHeight, window?.innerHeight ? window.innerHeight * 0.85 : 660) }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {/* 리사이즈 핸들 */}
          <div
            onMouseDown={onResizeStart}
            className="absolute top-0 left-0 w-6 h-6 z-50 cursor-nwse-resize flex items-center justify-center opacity-40 hover:opacity-100 transition-opacity"
            title="드래그하여 크기 조절"
          >
            <svg width="10" height="10" viewBox="0 0 10 10" className="text-gray-400 dark:text-gray-500 rotate-90">
              <path d="M1 9L9 1M4 9L9 4M7 9L9 7" stroke="currentColor" strokeWidth="1.5" fill="none" />
            </svg>
          </div>

          <button
            onClick={() => setIsOpen(false)}
            className="absolute top-4 right-4 z-50 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-gray-500 dark:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          {!authenticated ? (
            <div className="flex-1 p-6 flex flex-col items-center justify-center relative">
              <div className="absolute inset-0 bg-gradient-to-b from-cyan-50 dark:from-cyan-900/20 to-transparent pointer-events-none" />
              <form onSubmit={handleLogin} className="w-full max-w-[280px] relative z-10">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-100 to-blue-100 dark:from-cyan-500/20 dark:to-blue-500/20 flex items-center justify-center mx-auto mb-4 border border-cyan-200 dark:border-cyan-500/30">
                    <Lock className="w-8 h-8 text-cyan-600 dark:text-cyan-400" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">NAS 접근 권한</h2>
                  <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                    NAS 실데이터 근거 기반 답변을 위해<br />최초 대화 시작 시 보안 인가가 필요합니다.
                  </p>
                </div>
                <div className="space-y-4">
                  <input
                    type="password"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    placeholder="비밀번호 입력"
                    autoFocus
                    className="w-full px-4 py-3.5 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white outline-none focus:border-cyan-500 transition-all text-center tracking-widest text-sm shadow-inner"
                  />
                  {error && <p className="text-red-400 text-xs text-center animate-pulse">{error}</p>}
                  <button
                    type="submit"
                    className="w-full py-3.5 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-bold hover:from-cyan-500 hover:to-blue-500 transition-all text-sm shadow-lg shadow-cyan-500/20"
                  >
                    데이터 접근 승인
                  </button>
                </div>
              </form>
            </div>
          ) : (
            <div className="flex-1 flex h-full overflow-hidden">
              {/* NAS 파일 탐색기 (토글) */}
              <AnimatePresence>
                {showFilePicker && (
                  <motion.div
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: 200, opacity: 1 }}
                    exit={{ width: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="border-r border-white/10 overflow-hidden shrink-0"
                    style={{ width: 200 }}
                  >
                    <NasFilePicker
                      onSelect={(path) => { setSelectedFile(path); }}
                      selectedPath={selectedFile}
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* 채팅 영역 */}
              <div className="flex-1 flex flex-col overflow-hidden relative pt-10">
                {/* 채팅 헤더 */}
                <div className="absolute top-0 left-0 right-0 flex justify-between items-center px-3 py-2 border-b border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-black/40 z-40 h-10">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-md bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-sm">
                      <Lock className="w-2.5 h-2.5 text-white" />
                    </div>
                    <span className="text-[10px] font-bold text-cyan-600 dark:text-cyan-400 tracking-wider">NAS SECURE CHAT</span>
                    {selectedFile && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-cyan-500/10 text-cyan-500 dark:text-cyan-400 border border-cyan-500/20 max-w-[120px] truncate">
                        📄 {selectedFile.split('/').pop()}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 pr-10">
                    {/* NAS 파일 탐색기 토글 버튼 */}
                    <button
                      onClick={() => setShowFilePicker(!showFilePicker)}
                      className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded-lg border transition-colors ${
                        showFilePicker
                          ? 'bg-cyan-500/20 border-cyan-500/30 text-cyan-400'
                          : 'bg-white/5 border-white/10 text-gray-400 hover:text-gray-200 hover:bg-white/10'
                      }`}
                      title="NAS 파일 탐색기 열기/닫기"
                    >
                      <FolderOpen className="w-3 h-3" />
                      <span>NAS</span>
                    </button>
                    {selectedFile && (
                      <button
                        onClick={() => setSelectedFile('')}
                        className="text-[9px] text-gray-500 hover:text-red-400 px-1.5 py-1 rounded hover:bg-red-500/10 transition-colors"
                        title="파일 선택 해제"
                      >✕</button>
                    )}
                    <button
                      onClick={() => { setAuthenticated(false); localStorage.removeItem('rag_chat_auth'); }}
                      className="text-[10px] bg-red-50 dark:bg-red-500/10 text-red-500 dark:text-red-400 border border-red-100 dark:border-transparent px-2 py-1 rounded hover:bg-red-100 dark:hover:bg-red-500/20 transition-colors"
                    >
                      연결 해제
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-hidden relative">
                  <OmniChatPanel
                    compact={false}
                    selectedFile={selectedFile || undefined}
                    className="border-0 rounded-none h-full bg-transparent max-h-full"
                  />
                </div>
              </div>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
